package com.fireavert.fire_risk.client.presentation

import com.fireavert.cache.client.Preferences
import com.fireavert.common.TableColumn
import com.fireavert.common.Try
import com.fireavert.fire_risk.client.ClientTenantFireRiskRepository
import com.fireavert.fire_risk.data_models.RiskTableColumns
import com.fireavert.fire_risk.data_models.TenantFireRiskDataModel
import com.fireavert.logging.Logger
import com.fireavert.properties.shared.client.ClientPropertyRepository
import com.fireavert.reports_page.reports_page.client.ReportsPageRepository

class RiskTableInteractor(
    private val screen: FireRiskScreen,
    private val propertyRepository: ClientPropertyRepository,
    private val reportsPageRepository: ReportsPageRepository,
    private val clientTenantFireRiskRepository: ClientTenantFireRiskRepository,
    private val logger: Logger,
    private val preferences: Preferences
) {
    fun sortTrackingRecordsByColumn(
        columns: Array<TableColumn>,
        fireRiskData: List<TenantFireRiskDataModel>,
    ): List<TenantFireRiskDataModel> {
        val sortColumn =
            columns.firstOrNull() { it.sortOrder != TableColumn.SortOrder.Neutral } ?: return fireRiskData

        return when (sortColumn.text) {
            RiskTableColumns.RISK_LEVEL.columnName -> {
                TableColumn.sortList(false, sortColumn, fireRiskData) { it.riskRating }
            }

            RiskTableColumns.PROPERTY_NAME.columnName -> {
                TableColumn.sortList(true, sortColumn, fireRiskData) { it.propertyName }
            }

            RiskTableColumns.UNIT.columnName -> {
                // Fix: Sort by managementCompany instead of phone
                TableColumn.sortList(true, sortColumn, fireRiskData) { it.unitNumber }
            }

            else -> fireRiskData
        }
    }

    suspend fun onLoad() {
        screen.loading = true

        screen.highRiskTenantData = when (val maybe =
            clientTenantFireRiskRepository.getTenantFireRiskAllProperties(preferences.globalProperty)) {
            is Try.Error -> {
                logger.e("In RiskTableInteractor onLoad")
                logger.e("Error getting fire risk tenants: ${maybe.exception.message}")
                screen.loading = false
                return
            }

            is Try.Success -> {
                screen.loadingHighRiskTenants = false
                getMostRecentTenantRecords(maybe.value)
            }
        }
    }

    /**
     * Processes a list of tenant data, returning a new list containing only the
     * most recent record for each unique tenant.
     *
     * A unique tenant is identified by the combination of its propertyId and unitNumber.
     * "Most recent" is determined by the 'dateAdded' timestamp.
     *
     * @param data The raw list of TenantFireRiskDataModel, which may contain duplicates.
     * @return A list of TenantFireRiskDataModel with no duplicate tenants, where each
     *         entry is the one with the latest timestamp.
     */
    private fun getMostRecentTenantRecords(
        data: List<TenantFireRiskDataModel>
    ): List<TenantFireRiskDataModel> {
        // 1. Group the data by a unique tenant key.
        //    This creates a Map where the key is "propertyId-unitNumber" and the
        //    value is a list of all records for that specific tenant.
        return data.groupBy { "${it.propertyId}-${it.unitNumber}" }
            // 2. We only care about the lists of records, not the keys.
            //    .values gives us a Collection of these lists.
            .values
            // 3. For each list of records for a single tenant, find the one
            //    with the maximum 'dateAdded'. maxByOrNull is perfect for this.
            //    mapNotNull will automatically discard any null results (which
            //    could happen if a group was empty, though groupBy won't create empty groups).
            .mapNotNull { tenantsForOneUnit ->
                tenantsForOneUnit.maxByOrNull { it.dateAdded }
            }
    }

    suspend fun updateData(propertyId: Int?) {
        screen.propertyId = propertyId
        screen.loading = true
        screen.loadingYearlyEvents = true
        screen.loadingAllTimeEvents = true
        screen.loadingHighRiskTenants = true

        val eventsOverTimeCount = when (val maybe = reportsPageRepository.getPropertyEventsOverTime(propertyId)) {
            is Try.Error -> {
                screen.loading = false
                return
            }

            is Try.Success -> {
                maybe.value
            }
        }

        val firesAvertedCount = when (val maybe = reportsPageRepository.getFiresAvertedCount(propertyId)) {
            is Try.Error -> {
                screen.loading = false
                return
            }

            is Try.Success -> {
                maybe.value
            }
        }

        val sensorTypes = when (val maybe = propertyRepository.getSensorTypesForProperty(propertyId)) {
            is Try.Error -> {
                screen.loading = false
                return
            }

            is Try.Success -> {
                maybe.value
            }
        }
        screen.availableDeviceTypes = sensorTypes

        screen.firesAvertedCount = firesAvertedCount
        screen.yearStartedUsingFireAvert = eventsOverTimeCount.yearStarted
        screen.lastFiveYearClaims = eventsOverTimeCount.fireClaimData
        screen.loading = false
    }
}